import { css, styled } from 'styled-components';

import ButtonProps from 'styled/components/Button/Button.types';
import { themed } from 'styled/helpers';

const StyledButton = styled.button<{
  $allowVisited: Exclude<ButtonProps['allowVisited'], undefined>;
  $size: Exclude<ButtonProps['size'], undefined>;
  $variant: ButtonProps['variant'];
}>`
  --button-underline-offset: 0.125em;
  align-items: center;
  border-radius: ${themed('component/button/radius')};
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  font-family: ${themed('component/button/text/font')};
  font-weight: ${themed('component/button/text/weight')};
  justify-content: center;
  line-height: ${themed('component/button/text/lineHeight')};
  outline-offset: 2px;
  text-transform: ${themed('component/button/text/case')};
  transition-duration: 0.25s;
  transition-property: background, border-color, color;
  transition-timing-function: ease-out;
  user-select: none;
  vertical-align: middle;
  white-space: nowrap;

  ${props => {
    switch (props.$variant) {
      case 'filled':
        return css`
          background: ${themed('component/button/bg/color/filled/default')};
          border: none;
          color: ${themed('component/button/text/color/filled/default')};
          text-decoration: none;

          ${props.$allowVisited &&
          css`
            &:visited {
              background: ${themed('component/button/bg/color/filled/visited')};
              border-color: ${themed('component/button/border/color/filled/visited')};
              color: ${themed('component/button/text/color/filled/visited')};
            }
          `}

          &:disabled {
            background: ${themed('component/button/bg/color/filled/disabled')};
            border-color: ${themed('component/button/border/color/filled/disabled')};
            color: ${themed('component/button/text/color/filled/disabled')};
          }

          @media (hover) {
            &:hover:not(:disabled) {
              background: ${themed('component/button/bg/color/filled/hover')};
              border-color: ${themed('component/button/border/color/filled/hover')};
              color: ${themed('component/button/text/color/filled/hover')};
            }
          }
        `;
      case 'outlined':
        return css`
          background: transparent;
          border: ${themed('component/button/border/size')} solid ${themed('component/button/border/color/outlined/default')};
          color: ${themed('component/button/text/color/outlined/default')};
          text-decoration: none;

          ${props.$allowVisited &&
          css`
            &:visited {
              border-color: ${themed('component/button/border/color/outlined/visited')};
              color: ${themed('component/button/text/color/outlined/visited')};
            }
          `}

          &:disabled {
            border-color: ${themed('component/button/border/color/outlined/disabled')};
            color: ${themed('component/button/text/color/outlined/disabled')};
          }

          @media (hover) {
            &:hover:not(:disabled) {
              border-color: ${themed('component/button/border/color/outlined/hover')};
              color: ${themed('component/button/text/color/outlined/hover')};
            }
          }
        `;
      case 'text':
        return css`
          background: none;
          border-radius: 0;
          border: none;
          color: ${themed('component/button/text/color/text/default')};

          ${props.$allowVisited &&
          css`
            &:visited {
              color: ${themed('component/button/text/color/text/visited')};
            }
          `}

          &:disabled {
            color: ${themed('component/button/text/color/text/disabled')};
          }

          @media (hover) {
            &:hover:not(:disabled) {
              color: ${themed('component/button/text/color/text/hover')};
            }
          }
        `;
    }
  }}

  ${props => {
    switch (props.$size) {
      case 'xsmall':
        return css`
          font-size: ${themed('component/button/text/size/xsmall')};
          padding: ${props.$variant === 'outlined'
            ? css`calc(${themed('component/button/padding/y/xsmall')} - ${themed('component/button/border/size')})`
            : themed('component/button/padding/y/xsmall')};
        `;
      case 'small':
        return css`
          font-size: ${themed('component/button/text/size/small')};
          padding: ${props.$variant === 'outlined'
            ? css`calc(${themed('component/button/padding/y/small')} - ${themed('component/button/border/size')})`
            : themed('component/button/padding/y/small')};
        `;
      case 'base':
        return css`
          font-size: ${themed('component/button/text/size/base')};
          padding: ${props.$variant === 'outlined'
              ? css`calc(${themed('component/button/padding/y/base')} - ${themed('component/button/border/size')})`
              : themed('component/button/padding/y/base')}
            ${props.$variant === 'text' ? 0 : themed('component/button/padding/x/base')};
        `;
    }
  }}

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${themed('alias/focus/outline/color')};
  }
`;

export const StyledButtonContent = styled.span<{
  $variant: ButtonProps['variant'];
}>`
  align-items: center;
  display: inline-flex;
  gap: ${themed('global/space/xs')};
  min-height: ${themed('global/icon/size/small')};

  ${props =>
    props.$variant === 'text' &&
    css`
      border-bottom: ${themed('component/button/border/size')} solid currentColor;
      margin-bottom: calc(-1 * (${themed('component/button/border/size')} + var(--button-underline-offset)));
      padding-bottom: var(--button-underline-offset);
    `}
`;

export default StyledButton;
